import { useState, useEffect } from "react"
import { CurrencyHelper } from "~/helpers/currency_helper"
import { Button } from "~/react/ui/button"
import { SheetHeader, SheetTitle, SheetFooter } from "~/react/ui/sheet"
import {
  Tabs,
  TabsList,
  TabsListItem,
  TabsTrigger,
  TabsContent,
} from "~/react/ui/tabs"
import { Icon } from "~/react/ui/icon"
import { Tag } from "~/react/ui/tag"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuAction,
} from "~/react/ui/dropdown-menu"
import { getCsrfToken } from "~/lib/utils"
import { email_domains_health_checks_path } from "~/lib/routes"
import { Turbo } from "@hotwired/turbo-rails"
import type { DomainData } from "./domains_filters"
import HealthTab from "./tabs/health_tab"
import DomainDetailsTab from "./tabs/domain_details_tab"

type DomainSidepanelProps = {
  domain: DomainData
  onHealthCheckTriggered?: () => void
  onDomainUpdated?: (updatedDomain: DomainData) => void
}

type FormData = {
  redirectUrl: string
  owner: {
    first_name: string
    last_name: string
    email: string
    company: string
    address: string
    postal_code: string
    city: string
    country: string
  }
}

const formatDate = (isoDate: string | null | undefined): string => {
  if (!isoDate) return ""
  try {
    return new Date(isoDate).toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
  } catch {
    return ""
  }
}

export default function DomainSidepanel({
  domain: initialDomain,
  onHealthCheckTriggered,
  onDomainUpdated,
}: DomainSidepanelProps) {
  const [domain, setDomain] = useState<DomainData>(initialDomain)
  const [isRunningHealthCheck, setIsRunningHealthCheck] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [activeTab, setActiveTab] = useState(
    initialDomain.type === "managed" ? "details" : "email_accounts",
  )
  const [redirectUrlTouched, setRedirectUrlTouched] = useState(false)
  const [redirectUrlLogo, setRedirectUrlLogo] = useState<string | null>(null)
  const [healthRefreshToken, setHealthRefreshToken] = useState(0)

  const [formData, setFormData] = useState<FormData>({
    redirectUrl: "",
    owner: {
      first_name: "",
      last_name: "",
      email: "",
      company: "",
      address: "",
      postal_code: "",
      city: "",
      country: "",
    },
  })

  const isHealthCheckInProgress = isRunningHealthCheck

  // Keep domain state in sync when parent updates it (e.g., via push)
  useEffect(() => {
    setDomain(initialDomain)
  }, [initialDomain])

  // Force refresh of Health tab when incoming domain health changes
  useEffect(() => {
    setHealthRefreshToken((token) => token + 1)
  }, [initialDomain.health])

  // Fetch full domain data on mount
  useEffect(() => {
    const fetchDomainData = async () => {
      try {
        const response = await fetch(
          `/email-domains/${encodeURIComponent(initialDomain.domain)}`,
          {
            headers: {
              Accept: "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            credentials: "same-origin",
          },
        )

        if (response.ok) {
          const data = await response.json()
          setDomain(data)

          // Initialize form with fetched data
          const newFormData: FormData = {
            redirectUrl: data.redirect_url || "",
            owner: {
              first_name: data.owner?.first_name || "",
              last_name: data.owner?.last_name || "",
              email: data.owner?.email || "",
              company: data.owner?.company || "",
              address: data.owner?.address || "",
              postal_code: data.owner?.postal_code || "",
              city: data.owner?.city || "",
              country: data.owner?.country || "",
            },
          }
          setFormData(newFormData)

          // Set logo if redirect URL exists
          if (data.redirect_url) {
            try {
              const urlObj = new URL(data.redirect_url)
              const urlDomain = urlObj.hostname.replace("www.", "")
              setRedirectUrlLogo(`https://company-logo.hunter.io/${urlDomain}`)
            } catch {
              setRedirectUrlLogo(null)
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch domain data:", error)
      }
    }

    fetchDomainData()
  }, [initialDomain.domain])

  // Update active tab if details tab is not available for non-managed domains
  useEffect(() => {
    if (domain.type !== "managed" && activeTab === "details") {
      setActiveTab("email_accounts")
    }
  }, [domain.type, activeTab])

  const getStatusInfo = () => {
    const allActive = domain.status.inactive === 0
    const allInactive = domain.status.active === 0

    if (allActive) {
      return {
        label: i18n.t("js.email_accounts_center.domains_table.status.active"),
        variant: "success" as const,
      }
    }
    if (allInactive) {
      return {
        label: i18n.t("js.email_accounts_center.domains_table.status.inactive"),
        variant: "danger" as const,
      }
    }
    return {
      label: i18n.t("js.email_accounts_center.domains_table.status.mixed"),
      variant: "warning" as const,
    }
  }

  const statusInfo = getStatusInfo()

  const handleRunHealthCheck = async () => {
    setIsRunningHealthCheck(true)
    try {
      const response = await fetch(email_domains_health_checks_path(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({
          domain: domain.domain,
        }),
      })

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }))
        throw new Error(
          error.error ||
            i18n.t("js.email_accounts_center.errors.health_check_failed"),
        )
      }

      await response.json()

      setHealthRefreshToken((token) => token + 1)

      App.displayFlash(
        "success",
        i18n.t("js.email_accounts_center.actions.health_check_started"),
      )

      if (onHealthCheckTriggered) {
        onHealthCheckTriggered()
      }
    } catch (error) {
      console.error("Health check failed:", error)
      App.displayFlash(
        "error",
        error instanceof Error
          ? error.message
          : i18n.t("js.email_accounts_center.errors.health_check_failed"),
      )
      setIsRunningHealthCheck(false)
    }
  }

  const handleCancelRenewal = async () => {
    if (domain.type !== "managed") return

    const confirmed = window.confirm(
      i18n.t("js.email_accounts_center.bulk.cancel_renewal_confirm", {
        count: 1,
      }),
    )
    if (!confirmed) return

    try {
      const response = await fetch(
        `/email-domains/${encodeURIComponent(domain.domain)}/subscription`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            Accept: "text/vnd.turbo-stream.html, application/json",
            "X-CSRF-Token": getCsrfToken(),
          },
          credentials: "same-origin",
        },
      )

      if (response.ok) {
        const contentType = response.headers.get("content-type")
        if (contentType?.includes("text/vnd.turbo-stream.html")) {
          const html = await response.text()
          Turbo.renderStreamMessage(html)
        }

        if (App?.displayFlash) {
          App.displayFlash(
            "success",
            i18n.t("js.email_accounts_center.bulk.cancel_renewal_success", {
              count: 1,
            }),
          )
        }

        // Notify parent to refresh
        if (onDomainUpdated) {
          onDomainUpdated({ ...domain, status: { ...domain.status } })
        }
      } else {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }))
        alert(
          error.error ||
            i18n.t("js.email_accounts_center.bulk.cancel_renewal_error"),
        )
      }
    } catch (err) {
      alert(i18n.t("js.email_accounts_center.bulk.cancel_renewal_error"))
    }
  }

  const isValidUrl = (url: string): boolean => {
    if (!url) return true
    try {
      const urlObj = new URL(url)
      return urlObj.protocol === "http:" || urlObj.protocol === "https:"
    } catch {
      return false
    }
  }

  const isRedirectUrlInvalid =
    redirectUrlTouched &&
    formData.redirectUrl.length > 0 &&
    !isValidUrl(formData.redirectUrl)

  const handleRedirectUrlBlur = () => {
    setRedirectUrlTouched(true)
    if (formData.redirectUrl) {
      try {
        const urlObj = new URL(formData.redirectUrl)
        const urlDomain = urlObj.hostname.replace("www.", "")
        setRedirectUrlLogo(`https://company-logo.hunter.io/${urlDomain}`)
      } catch {
        setRedirectUrlLogo(null)
      }
    } else {
      setRedirectUrlLogo(null)
    }
  }

  const clearRedirectUrl = () => {
    setFormData((prev) => ({ ...prev, redirectUrl: "" }))
    setRedirectUrlLogo(null)
  }

  const updateOwnerField = (field: keyof FormData["owner"], value: string) => {
    setFormData((prev) => ({
      ...prev,
      owner: { ...prev.owner, [field]: value },
    }))
  }

  const handleSave = async () => {
    if (domain.type !== "managed") {
      // Can only update managed domains
      return
    }

    setIsSaving(true)
    try {
      const response = await fetch(
        `/email-domains/${encodeURIComponent(domain.domain)}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "X-CSRF-Token": getCsrfToken(),
          },
          credentials: "same-origin",
          body: JSON.stringify({
            redirect_url: formData.redirectUrl,
            owner: formData.owner,
          }),
        },
      )

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }))
        throw new Error(
          error.error ||
            i18n.t("js.email_accounts_center.domain.details.save_error"),
        )
      }

      const updatedDomain = await response.json()
      setDomain(updatedDomain)

      if (onDomainUpdated) {
        onDomainUpdated(updatedDomain)
      }

      App.displayFlash(
        "success",
        i18n.t("js.email_accounts_center.domain.details.save_success"),
      )
    } catch (error) {
      console.error("Save failed:", error)
      App.displayFlash(
        "error",
        error instanceof Error
          ? error.message
          : i18n.t("js.email_accounts_center.domain.details.save_error"),
      )
    } finally {
      setIsSaving(false)
    }
  }

  const createdAt = formatDate(domain.created_at)
  const expireAt = formatDate(domain.expire_at)
  const baselineText =
    createdAt && expireAt
      ? `${i18n.t("js.email_accounts_center.domain.details.created_at")} ${createdAt} · ${i18n.t("js.email_accounts_center.domain.details.renew_on")} ${expireAt}`
      : createdAt
        ? `${i18n.t("js.email_accounts_center.domain.details.created_at")} ${createdAt}`
        : ""

  return (
    <article>
      <div className="lead-details">
        <SheetHeader className="p-0">
          <header className="lead-details__header">
            <div className="lead-details__heading">
              <SheetTitle className="lead-details__name mb-0">
                {domain.domain}
              </SheetTitle>
              <div className="lead-details__baseline">
                <span className="lead-details__baseline-item">
                  {baselineText}
                </span>
              </div>
            </div>
          </header>
        </SheetHeader>

        <div className="lead-details__actions mb-4 d-flex align-items-center gap-2">
          <Tag variant={statusInfo.variant}>{statusInfo.label}</Tag>
          {domain.provider && (
            <Tag>
              {i18n.t(
                `js.email_accounts_center.domains_table.providers.${domain.provider}`,
              ) || domain.provider}
            </Tag>
          )}
        </div>

        <div className="lead-details__actions d-flex align-items-center gap-2">
          <Button
            size="sm"
            type="button"
            onClick={() => {
              // TODO: Implement add email accounts action
            }}
          >
            {i18n.t(
              "js.email_accounts_center.domain.actions.add_email_accounts",
            )}
          </Button>

          <Button
            size="sm"
            type="button"
            onClick={handleRunHealthCheck}
            disabled={isHealthCheckInProgress}
            data-loading={isHealthCheckInProgress ? "true" : "none"}
          >
            <Icon
              icon="shield-check"
              variant="regular"
              size="sm"
              aria-hidden="true"
            />
            {i18n.t(
              "js.email_accounts_center.email_accounts_table.actions.run_health_check",
            )}
          </Button>

          {domain.type === "managed" && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="default"
                  size="sm"
                  iconOnly
                  type="button"
                  aria-label={i18n.t(
                    "js.email_accounts_center.actions.more_actions_aria",
                  )}
                >
                  <Icon
                    icon="ellipsis-h"
                    variant="regular"
                    size="sm"
                    aria-hidden="true"
                  />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="dropdown-menu-end">
                <DropdownMenuAction type="button" onClick={handleCancelRenewal}>
                  {i18n.t(
                    "js.email_accounts_center.email_accounts_table.actions.cancel_renewal",
                  )}
                </DropdownMenuAction>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
      >
        <TabsList
          color="lightergrey"
          size="md"
          className="pt-6 px-6"
        >
          {domain.type === "managed" && (
            <TabsListItem value="details">
              <TabsTrigger value="details">
                {i18n.t("js.email_accounts_center.domain.details.title")}
              </TabsTrigger>
            </TabsListItem>
          )}
          <TabsListItem value="email_accounts">
            <TabsTrigger value="email_accounts">
              {i18n.t("js.email_accounts_center.domain.email_accounts.title")}
            </TabsTrigger>
          </TabsListItem>
          <TabsListItem value="health">
            <TabsTrigger value="health">
              {i18n.t("js.email_accounts_center.domain.health.title")}
            </TabsTrigger>
          </TabsListItem>
        </TabsList>

        {domain.type === "managed" && (
          <TabsContent value="details" className="p-6">
            <DomainDetailsTab
              redirectUrl={formData.redirectUrl}
              redirectUrlLogo={redirectUrlLogo}
              redirectUrlTouched={redirectUrlTouched}
              isRedirectUrlInvalid={isRedirectUrlInvalid}
              owner={formData.owner}
              onRedirectUrlChange={(value) => {
                setFormData((prev) => ({ ...prev, redirectUrl: value }))
                if (!value) {
                  setRedirectUrlTouched(false)
                }
              }}
              onRedirectUrlBlur={handleRedirectUrlBlur}
              onRedirectUrlClear={() => {
                clearRedirectUrl()
                setRedirectUrlTouched(false)
              }}
              onOwnerFieldChange={updateOwnerField}
              i18n={i18n}
            />
          </TabsContent>
        )}

        <TabsContent value="email_accounts" className="p-6">
          <h3
            className="text-sm mb-4"
            dangerouslySetInnerHTML={{
              __html: i18n.t(
                "js.email_accounts_center.domain.email_accounts.count_html",
                {
                  pretty_count: CurrencyHelper.numberWithDelimiter(
                    domain.email_accounts_count,
                  ),
                  count: domain.email_accounts_count,
                },
              ),
            }}
          />
          <ul className="lead-campaigns">
            {domain.email_accounts && domain.email_accounts.length > 0 ? (
              domain.email_accounts.map((email) => (
                <li key={email} className="lead-campaign">
                  <div className="lead-campaign__heading">
                    <strong className="lead-campaign__name">{email}</strong>
                    <div className="lead-campaign__metas">
                      {/* TODO: Add owner name */}
                    </div>
                  </div>
                  <Tag variant="success">Active</Tag>
                </li>
              ))
            ) : (
              <li className="text-xs text-body-secondary">
                No email accounts
              </li>
            )}
          </ul>
        </TabsContent>

        <TabsContent value="health" className="p-0">
          {domain.domain && (
            <HealthTab
              key={healthRefreshToken}
              domain={domain.domain}
              health={domain.health ?? null}
            />
          )}
        </TabsContent>
      </Tabs>

      {activeTab === "details" && domain.type === "managed" && (
        <SheetFooter>
          <Button
            variant="primary"
            type="button"
            onClick={handleSave}
            disabled={isSaving || isRedirectUrlInvalid}
            data-loading={isSaving ? "true" : "none"}
          >
            {i18n.t("js.email_accounts_center.domain.details.save")}
          </Button>
        </SheetFooter>
      )}
    </article>
  )
}
